import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaBriefcase, FaFacebook, FaLinkedin, FaEdit, FaPaperPlane } from 'react-icons/fa';
import { useGlobalState } from '../contexts/GlobalContext';
import { FaXTwitter } from 'react-icons/fa6';

function MyProfile() {
  const { state, dispatch } = useGlobalState();
  const user = state.user || {};
  const brokerageName =
    typeof user.brokerage === "object"
      ? user.brokerage.officeBusinessName
      : user.brokerage;
  const listings = state.properties?.filter((property) => String(property.creatorID) === String(user.id)) || [];

  const [newPost, setNewPost] = useState('');
  const [posts, setPosts] = useState(user.posts || []);

  useEffect(() => {
    setPosts(user.posts || []);
  }, [user.posts]);

  // ✅ Calculate years of experience from startDate
  const calculateYearsExperience = (startDate) => {
    if (!startDate) return "N/A"; // Handle missing startDate
    const startYear = new Date(startDate).getFullYear();
    const currentYear = new Date().getFullYear();
    return currentYear - startYear;
  };

  const yearsExperience = calculateYearsExperience(user.startDate);

  const handlePostSubmit = () => {
    if (!newPost.trim()) return;

    const post = {
      id: Date.now(),
      content: newPost,
      timestamp: new Date().toLocaleTimeString(),
    };

    dispatch({
      type: "ADD_POST",
      payload: { userId: user.userId, post },
    });

    setPosts([post, ...posts]);
    setNewPost('');
  };

  return (
    <div className="container mt-4">
      <div className="card shadow-sm p-4">
        {/* Profile Header */}
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <img 
              src={user.profilePicture || "https://placehold.co/150"}  
              alt="Profile" 
              className="rounded-circle me-3"
              style={{ width: "100px", height: "100px", objectFit: "cover" }} 
            />
            <div>
              <h3 className="mb-1">{user.name}</h3>
               <p className="text-muted">{user.title} at {brokerageName}</p>
              
              {/* Conditional Rendering for Year(s) Experience */}
              {yearsExperience !== null && (
                <p className="text-muted">
                  <strong>Experience:</strong> • {yearsExperience} {yearsExperience > 1 ? "Years" : "Year"}
                </p>
              )}
            </div>
          </div>

          <Link to="/edit-profile" state={{ user }}>
            <button className="btn btn-outline-primary">
              <FaEdit className="me-1" /> Edit Profile
            </button>
          </Link>
        </div>

        {/* Contact Info */}
        <div className="mt-3">
          <p><FaPhone className="me-2 text-primary" /> {user.cellPhone || "N/A"}</p>
          <p><FaEnvelope className="me-2 text-primary" /> {user.email || "N/A"}</p>
          <p><FaMapMarkerAlt className="me-2 text-primary" /> {user.location || "Your Location"}</p>
        </div>

        {/* Specializations */}
        <div className="mt-3">
          <h5>Specializations</h5>
          {user.specializations?.length > 0 ? (
            <ul className="list-group">
              {user.specializations.map((specialty, index) => (
                <li key={index} className="list-group-item">
                  <FaBriefcase className="me-2 text-success" /> {specialty}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">No specializations listed.</p>
          )}
        </div>

        {/* Active Listings */}
        <div className="mt-3">
          <h5>My Listings</h5>
          {listings.length > 0 ? (
            <div className="row row-cols-2 row-cols-md-4 row-cols-lg-8 g-2">
              {listings.map((listing) => (
                <div key={listing.parcelNumber} className="col">
                  <Link to={`/property/${listing.parcelNumber}`} className="text-decoration-none text-dark">
                    <div className="card shadow-sm p-2" style={{ fontSize: "0.8rem" }}>
                      
                      {/* Property Image */}
                      <img 
                        src={listing.imageUrl} 
                        className="card-img-top" 
                        alt={listing.streetAddress} 
                        style={{ height: "100px", objectFit: "cover", borderRadius: "5px" }}
                      />

                      {/* Property Details */}
                      <div className="card-body p-2 text-center">
                        <h6 className="card-title mb-1">{listing.streetAddress}</h6>
                        <p className="text-muted mb-1">{listing.city}, {listing.state}</p>
                        <p className="text-primary mb-1"><strong>${listing.listPrice}</strong></p>

                        {/* Three-Column Grid for Compact Details */}
                        <div className="row g-0">
                          <div className="col-4">
                            <p className="mb-0"><strong>{listing.bedrooms} 🛏️</strong></p>
                          </div>
                          <div className="col-4">
                            <p className="mb-0"><strong>{listing.bathrooms} 🛁</strong></p>
                          </div>
                          <div className="col-4">
                            <p className="mb-0"><strong>{listing.livingSqFt} sqft</strong></p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted">No active listings yet.</p>
          )}
        </div>




        {/* Post Feed */}
        <div className="mt-3">
          <h5>Post Updates</h5>
          <div className="card p-3 mb-3">
            <textarea
              className="form-control"
              rows="2"
              placeholder="Share an update..."
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
            ></textarea>
            <button className="btn btn-primary mt-2 w-100" onClick={handlePostSubmit}>
              <FaPaperPlane className="me-1" /> Post
            </button>
          </div>

          {/* Display Posts */}
          {posts.length > 0 ? (
            <ul className="list-group">
              {posts.map((post) => (
                <li key={post.id} className="list-group-item">
                  {post.content} <small className="text-muted">({post.timestamp})</small>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">No updates yet.</p>
          )}
        </div>

        {/* Social Links */}
        <div className="mt-3 text-center">
          <h5>Connect with Me</h5>
          {user.socialLinks && (
            <>
              <a href={user.socialLinks.facebook || "#"} target="_blank" rel="noopener noreferrer" className="btn btn-primary me-2">
                <FaFacebook />
              </a>
              <a href={user.socialLinks.linkedin || "#"} target="_blank" rel="noopener noreferrer" className="btn btn-secondary me-2">
                <FaLinkedin />
              </a>
              <a href={user.socialLinks.x || "#"} target="_blank" rel="noopener noreferrer" className="btn btn-info">
                <FaXTwitter />
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyProfile;
